import Head from 'next/head'
import Link from 'next/link'

import Wave from '@/components/backgrounds/AboutUsWave'
import LinesLeft from '@/components/backgrounds/LinesLeft'
import LinesRight from '@/components/backgrounds/LinesRight'
import Carousel from '@/components/Carousel'
import AssemblyIcon from '@/components/icons/about-us/Assembly'
import CustomerServiceIcon from '@/components/icons/about-us/CustomerService'
import DistributionIcon from '@/components/icons/about-us/Distribution'
import KittingIcon from '@/components/icons/about-us/Kitting'
import LeadIcon from '@/components/icons/about-us/Lead'
import ProcurementIcon from '@/components/icons/about-us/Procurement'
import SiplifyIcon from '@/components/icons/about-us/Simplify'
import ScourcingIcon from '@/components/icons/about-us/Sourcing'
import SupportIcon from '@/components/icons/about-us/Support'
import SystemDesignIcon from '@/components/icons/about-us/SystemDesign'
import TechSupportIcon from '@/components/icons/about-us/TechSupport'
import Layout from '@/components/Layout'
import HeaderImg from '@/public/img/about-us/About-Us-Header.jpg'
import CommercialImg from '@/public/img/about-us/Commercial-Solutions.jpg'
import BrentImg from '@/public/img/about-us/leadership/Brent.jpg'
import EricImg from '@/public/img/about-us/leadership/Eric.jpg'
import JohnImg from '@/public/img/about-us/leadership/John.jpg'
import OpieImg from '@/public/img/about-us/leadership/Opie.jpg'
import PatImg from '@/public/img/about-us/leadership/Pat.jpg'
import SamImg from '@/public/img/about-us/leadership/Sam.jpg'
import TimImg from '@/public/img/about-us/leadership/Tim.jpg'
import MobileImg from '@/public/img/about-us/Mobile-Solutions.jpg'
import PaceTeamImg from '@/public/img/about-us/Pace-Team.jpg'
import RetailImg from '@/public/img/about-us/Retail-Solutions.jpg'


export default function AboutUs(): JSX.Element {
	return (
		<div className="overflow-hidden">
			<Head>
				<title key="page-title">About Us | Pace International</title>
			</Head>

			<section className="relative bg-gray-100">
				<div className="flex absolute bottom-0 items-center justify-center w-full">
					<div className="bg-white px-5 pt-2 mx-4 -mb-12 text-center rounded-t sm:mx-10 sm:-mb-6 sm:pt-3 sm:rounded-t-md md:px-8 lg:pt-4 lg:px-12 lg:rounded-t-lg xl:px-16">
						<p className="font-extrabold uppercase text-sm sm:text-lg md:text-xl">Our Mission</p>
						<p className="text-xs mt-0.5 sm:text-base lg:text-lg xl:text-xl">Leading, simplifying, and supporting the integration of hardware and content solutions <br className="hidden md:block"/>within a complex communications marketplace.</p>
						<div className="bg-green-400 rounded-full h-1 w-24 mx-auto mt-2 sm:w-32 sm:mt-3"></div>
					</div>
				</div>

				<video
					className="mx-auto w-full max-w-screen-2xl"
					controls={false}
					autoPlay
					playsInline
					muted
					loop
					poster={HeaderImg}
				>
					<source src="https://f.hubspotusercontent20.net/hubfs/1846077/PaceIntl-Website/About-Us-Header.mp4" type="video/mp4"/>
					<source src="https://f.hubspotusercontent20.net/hubfs/1846077/PaceIntl-Website/About-Us-Header.webm" type="video/webm"/>
					<source src="https://f.hubspotusercontent20.net/hubfs/1846077/PaceIntl-Website/About-Us-Header.avi" type="video/avi"/>
				</video>
			</section>


			<section className="container px-3 mt-20 mx-auto sm:mt-20 sm:px-8 md:mt-24 lg:px-14 lg:mt-32 xl:px-24 2xl:max-w-7xl">
				<div className="text-center">
					<p className="text-xl font-extrabold md:text-2xl lg:text-3xl xl:text-4xl">Connecting people to the world around them through content and hardware distribution</p>
					<p className="mt-4 text-sm md:text-base lg:text-lg lg:mt-4 xl:text-xl">We're a nationwide distributor with decades of experience managing a wide range of products, installations, and value adds for some of the most-recognized companies in the world. We specialize in connecting content to devices by offering quality products, leading brands, and exceptional service to all of our customers.</p>
				</div>

				<div className="mt-10 flex items-center justify-around bg-gradient-to-r from-white via-gray-100 to-white py-4 md:py-6 lg:py-10">
					<div className="flex flex-col items-center">
						<div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24">
							<LeadIcon/>
						</div>

						<p className="font-extrabold uppercase text-center mt-3 text-sm sm:text-lg lg:mt-6 lg:text-xl">Lead</p>
					</div>

					<div className="flex flex-col items-center">
						<div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24">
							<SiplifyIcon/>
						</div>

						<p className="font-extrabold uppercase text-center mt-3 text-sm sm:text-lg lg:mt-6 lg:text-xl">Simplify</p>
					</div>

					<div className="flex flex-col items-center">
						<div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24">
							<SupportIcon/>
						</div>

						<p className="font-extrabold uppercase text-center mt-3 text-sm sm:text-lg lg:mt-6 lg:text-xl">Support</p>
					</div>
				</div>
			</section>

			<section className="container mx-auto px-4 mt-20 sm:px-6 sm:mt-24 lg:mt-32 xl:px-8 2xl:max-w-7xl">
				<div className="text-center">
					<p className="text-xl font-extrabold md:text-2xl lg:text-3xl xl:text-4xl">When you have a challenge, Pace has a solution</p>
					<p className="mt-4 text-sm md:text-base lg:text-lg lg:mt-4 xl:text-xl">If you're looking to fulfill a complex request, fix procurement issues, navigate a new installation, or solve any number of mission-critical needs, then allow us to make your business our business.</p>
				</div>

				<div className="mt-12 sm:flex md:justify-between xl:mt-20">
					<Link href="/retail">
						<a className="relative block overflow-hidden rounded-lg shadow-md w-60 mx-auto sm:w-full">
							<img src={RetailImg} alt="Reail Solutions" />
							<p className="absolute bottom-0 left-0 right-0 mx-auto text-center uppercase text-white font-extrabold py-2 bg-black bg-opacity-60 sm:text-xs md:text-sm lg:text-base xl:text-lg">Retail Solutions</p>
						</a>
					</Link>

					<Link href="/commercial">
						<a className="relative block overflow-hidden rounded-lg shadow-md w-60 mx-auto mt-6 sm:mt-0 sm:w-full sm:ml-4 md:ml-7 lg:ml-9 xl:ml-12">
							<img src={CommercialImg}alt="Commercial Solutions" />
							<p className="absolute bottom-0 left-0 right-0 mx-auto text-center uppercase text-white font-extrabold py-2 bg-black bg-opacity-60 sm:text-xs md:text-sm lg:text-base xl:text-lg">Commercial Solutions</p>
						</a>
					</Link>

					<Link href="/mobile">
						<a className="relative block overflow-hidden rounded-lg shadow-md w-60 mx-auto mt-6 sm:mt-0 sm:w-full sm:ml-4 md:ml-7 lg:ml-9 xl:ml-12">
							<img src={MobileImg} alt="Mobile Solutions" />
							<p className="absolute bottom-0 left-0 right-0 mx-auto text-center uppercase text-white font-extrabold py-2 bg-black bg-opacity-60 sm:text-xs md:text-sm lg:text-base xl:text-lg">Mobile Solutions</p>
						</a>
					</Link>
				</div>
			</section>

			<section className="mt-12 bg-gray-100 px-3 py-9 sm:mt-16 sm:py-10 sm:px-8 md:py-12 lg:mt-24 lg:px-20 lg:py-16 xl:px-24">
				<div className="container mx-auto 2xl:max-w-7xl">
					<div className="text-center">
						<p className="text-xl font-bold md:text-2xl lg:text-3xl xl:text-4xl">What others call a value add, we call standard practice</p>
						<p className="text-sm mt-3 md:text-base lg:text-lg lg:mt-4 xl:text-xl">We know that no two companies – or projects – are alike. So, we take the details out of your hands by meeting one-on-one to discuss the intricacies of your request, then find a way to meet you with a flexible, budget-friendly solution that reduces your labor and turnaround time.</p>
					</div>

					<div className="grid grid-cols-2-auto gap-9 mt-9 md:grid-cols-4-auto md:gap-x-9 md:gap-y-20 md:mt-20">
						<div>
							<DistributionIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold sm:text-base">DISTRIBUTION</p>
								<p className="text-xs sm:text-sm lg:text-base">We ship out thousands of orders to businesses everyday.</p>
							</div>
						</div>

						<div>
							<ProcurementIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">PROCUREMENT</p>
								<p className="text-xs sm:text-sm lg:text-base">Keeping low costs without sacrificing the quality of all our products.</p>
							</div>
						</div>

						<div>
							<CustomerServiceIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">Customer Service</p>
								<p className="text-xs sm:text-sm lg:text-base">Supplying customers with product and service support</p>
							</div>
						</div>

						<div>
							<KittingIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">Kitting</p>
								<p className="text-xs sm:text-sm lg:text-base">On-site equipment delivery that's ready to go right from the kit.</p>
							</div>
						</div>

						<div>
							<ScourcingIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">SOURCING</p>
								<p className="text-xs sm:text-sm lg:text-base">We acquire goods and equipment from all across the world.</p>
							</div>
						</div>

						<div>
							<TechSupportIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">Tech Support</p>
								<p className="text-xs sm:text-sm lg:text-base">A team of technical wizards that know your products inside and out.</p>
							</div>
						</div>

						<div>
							<SystemDesignIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">System Design</p>
								<p className="text-xs sm:text-sm lg:text-base">Custom designed systems based on your property needs.</p>
							</div>
						</div>

						<div>
							<AssemblyIcon className="h-12 w-12 mx-auto sm:h-14 sm:w-14"/>
							<div className="text-center mt-3">
								<p className="text-sm uppercase font-extrabold lg:text-lg">ASSEMBLY</p>
								<p className="text-xs sm:text-sm lg:text-base">Fully stocked and capable warehouse for quick turnaround shipments.</p>
							</div>
						</div>
					</div>
				</div>
			</section>


			<section className="w-full relative mt-12 sm:mt-20 md:mt-24">
				<LinesRight className="absolute h-11 right-0 top-0 -mr-12 sm:h-16 md:-mr-10 lg:h-20 lg:-mr-4" />

				<LinesLeft className="hidden h-11 left-0 bottom-0 -ml-20 sm:h-16 md:block md:absolute md:-ml-16 lg:h-20 lg:-ml-4" />

				<div className="container relative mx-auto px-3 pt-20 sm:px-8 md:flex md:justify-between md:items-center md:pb-28 lg:pt-36 lg:pb-36 2xl:max-w-7xl">
					<div className="text-center md:w-1/2 md:text-left">
						<p className="text-xl font-extrabold md:text-2xl lg:text-3xl xl:text-4xl">Let us introduce you to <br className="hidden md:block"/>our team</p>
						<p className="mt-4 text-sm md:text-base lg:text-lg xl:text-xl">Pace has been family-owned since 1972 and our company culture has evolved throughout the past four decades to become a close-knit family of colleagues. With this strong teamwork and interconnectivity, we provide quick solutions and turnaround on projects to all of the partners we work with. Our happy family of employees translates to top quality results for your business.</p>
					</div>

					<img className="mt-12 rounded-lg shadow-md mx-auto md:w-1/2 md:mt-0 md:ml-12 lg:ml-14 xl:ml-16" src={PaceTeamImg} alt="Pace Family" />
				</div>
			</section>


			<section className="mt-12 relative overflow-hidden">
				<div className="absolute inset-0 -mb-1 transform translate-x-1/2">
					<Wave className="h-full transform -translate-x-1/2" />
				</div>

				<div className="relative py-12 sm:pt-14 sm:pb-12 md:pt-16 lg:pb-16 lg:pt-20 xl:pt-24">
					<p className="text-xl font-extrabold text-white text-center sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">Our Leadership Team</p>

					<div className="mt-3 container mx-auto px-8 sm:px-14 sm:mt-5 md:px-12 md:mt-8 lg:px-0 lg:mt-12 xl:px-32 2xl:px-0 2xl:max-w-7xl 2xl:mx-auto">
						<Carousel className="focus:ring-0 focus:ring-offset-0" options={{
							wrapAround: true,
							autoPlay: 10000,
							pageDots: false,
							cellAlign: 'left',
							percentPosition: false,
						}}>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40 xl:-mt-24 xl:w-40 xl:h-40">
									<img src={PatImg} alt="Pat Deutsch" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">Pat Deutsch</p>
									<p className="text-sm italic h-10 2xl:h-auto">Chief Executive Officer</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">Pat has worked in the family business his entire career. In 1992, he became Vice President of the Company- then in 1999, he became President when his father and founder of Pace International, Jim Deutsch, passed away. During Pat's tenure as President and now CEO, Pace has tripled in revenue, built new corporate headquarters, then expanded the facility further, purchased a major hardware and tool supply business and partnered with national hardware and content powerhouse, DISH®.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>
								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={OpieImg} alt="OPIE WILLIAMS" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">OPIE WILLIAMS</p>
									<p className="text-sm italic h-10 2xl:h-auto">President</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">As Pace's President, Opie delivers the vision and guidance needed to position the company as a leader in the multi-platform industry. His role at Pace includes the development of strategic initiatives across all Pace business units, executive level customer and vendor relationships, global distribution partnerships, and corporate leadership development. Opie focuses on deepening executive relationships with customers and developing new opportunities for Pace. He has been instrumental in guiding and leading his team with new opportunities and challenges.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>
								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={EricImg} alt="ERIC DEUTSCH" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">ERIC DEUTSCH</p>
									<p className="text-sm italic h-10 2xl:h-auto">Executive Vice President of Finance</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">Eric Deutsch, CPA joined the family business in May 2009. He became the third generation Deutsch to work at Pace International following his grandfather and founder, Jim Deutsch, and father and current CEO, Pat. As Vice President of Finance, Eric oversees Purchasing, Accounts Receivable, and Accounts Payable departments within Pace. Eric joined the Pace team after working for two years as a tax associate for a large public accounting firm where he gained experience doing tax returns, planning and research for large corporations, small partnerships as well as individuals.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>
								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={TimImg} alt="TIM DEUTSCH" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">TIM DEUTSCH</p>
									<p className="text-sm italic h-10 2xl:h-auto">Executive Vice President of Sales</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">Tim Deutsch, CPA joined the family business in November of 2012. Along with his brother, he became the third generation Deutsch to work at Pace following his grandfather and Founder, Jim Deutsch, and father and current CEO, Pat. As EVP of Sales, Tim works directly with the President and has utilized his past experience auditing privately held businesses to understand all aspects of Pace's day-to-day operations, from PCO and mobile divisions, to programming and purchasing.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>

								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={SamImg} alt="SAM SCHELL" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">SAM SCHELL</p>
									<p className="text-sm italic h-10 2xl:h-auto">Senior Vice President of Business Development</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">Sam works closely with Pace's national accounts, vendors, and internal teams to lead Pace sales and marketing initiatives. Sam has an extensive background in product development, sales and marketing, with over 16 years of telecom industry experience. Sam has been instrumental in implementing new business units for Pace, and most recently has led the Pace Mobile business unit to exceptional growth. Sam is an entrepreneur and a seasoned relationship-builder, helping to drive company value and bring products and service ideas to life.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>

								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={BrentImg} alt="BRENT AFSETH" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">BRENT AFSETH</p>
									<p className="text-sm italic h-10 2xl:h-auto">Vice President of Channel Partners</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>
								</div>
							</div>

							<div className="bg-gray-150 rounded-lg shadow-lg py-4 px-5 w-64 mt-12 flex flex-col items-center mx-2 md:mx-4 md:w-80 md:mt-20 2xl:w-96 2xl:mx-8">
								<div className="w-24 h-24 rounded-full overflow-hidden -mt-16 md:-mt-24 md:w-40 md:h-40">
									<img src={JohnImg} alt="JOHN SCHELL" />
								</div>

								<div className="mt-3.5">
									<p className="text-sm font-extrabold uppercase">JOHN SCHELL</p>
									<p className="text-sm italic h-10 2xl:h-auto">Vice President of Management Information Systems</p>

									<div className="relative text-xs mt-2">
										<p className="absolute inset-0">John works closely with vendors, logistics partners, and internal teams to optimize Pace's supply chain and evolve its strategic purchasing plan. He has extensive experience in international sourcing and trade, having spent over seven years in Asia heading up Pace's procurement operations. John has led the way in implementing new technologies and extracting business intelligence from data. As a dedicated team member and group leader, John is focused on improving internal systems and pricing strategy that best serves Pace's customers and vendors.</p>

										<p aria-hidden className="invisible">Brent Afseth began with Pace in 1998. His long-standing dedication and experience include a variety of projects. As a Warehouse Manager he implemented new processes to streamline warehouse efficiencies and shipping accuracy. He also has acted as a Senior Project Manager where he has built and maintained many valuable, long-standing relationships with customers and vendors as well as demonstrating a solid background in project planning and process implementation. Brent now leads Pace, overseeing daily operations, and developing policies and procedures that increase efficiencies and accuracy.</p>
									</div>
								</div>
							</div>
						</Carousel>
					</div>
				</div>
			</section>
		</div>
	)
}

AboutUs.Layout = Layout
